import React, { useState, useEffect } from "react";
import ChatPromptInput from "./ChatPromptInput";
import ChatModal from "./ChatModal";
import { AI_ROLES } from "../constants";
import { AIMessage } from "../types";
import { useChat } from "../../../apis/aiApi.hooks";
import { updateIntercom } from "../../../utils/Intercom";

const AIAssistant: React.FC = () => {
  const [showChatModal, setShowChatModal] = useState(false);
  const [messages, setMessages] = useState<AIMessage[]>([]);
  const [chatUuid, setChatUuid] = useState<string>();
  // need to use this error state variable on the frontend when needed.
  const [, setError] = useState<string>();
  const { sendMessage, isLoading, streamedText, setStreamedText, newChatUuid } =
    useChat();

  const handleCloseModal = () => {
    setShowChatModal(false);
    setMessages([]);
    setChatUuid(undefined);
    setStreamedText("");
  };

  useEffect(() => {
    if (showChatModal) {
      updateIntercom({
        hide_default_launcher: true,
      });
    } else {
      updateIntercom({
        hide_default_launcher: false,
      });
    }
  }, [showChatModal]);

  const handleSend = async (
    message: string,
    interruptionStatus?: "approved" | "rejected",
  ) => {
    setShowChatModal(true);

    // if interruptionStatus is not provided, add the user message to the messages array. If it's provided, we don't want to add it as a user message.
    if (!interruptionStatus) {
      setMessages((prevMessages) => [
        ...prevMessages,
        { role: AI_ROLES.USER, content: message },
      ]);
    }
    const response = await sendMessage({
      message,
      chatUuid,
      interruptionStatus,
    });

    let parsedData;
    let interruptionName;

    // if the assistant responds with an interruption, we want to add the interruption to the messages array with the interruptionName and data.
    if ("interruption" in response.response) {
      try {
        const data = response.response.interruption[0].rawItem.arguments;
        interruptionName = response.response.interruption[0].rawItem.name;
        parsedData = JSON.parse(data);
      } catch {
        setError("Error parsing AI interruption data");
      }
    }

    setMessages((prevMessages) => [
      ...prevMessages,
      {
        role: AI_ROLES.ASSISTANT,
        content: response.response.message,
        ...(parsedData && interruptionName
          ? { data: parsedData, interruptionName }
          : {}),
      },
    ]);
  };

  useEffect(() => {
    setChatUuid(newChatUuid ?? "");
  }, [newChatUuid]);

  return (
    <div className="tw-max-w-4xl gap-4">
      <div className="tw-flex tw-items-center tw-justify-between">
        <h2 className="tw-text-lg tw-py-2 tw-font-bold tw-text-neutral-800 tw-flex tw-items-center">
          Assistant
        </h2>
        <button
          className="tw-text-sm tw-text-blue-500"
          onClick={() => setShowChatModal(true)}
        >
          Open Assistant
        </button>
      </div>
      {showChatModal ? (
        <ChatModal
          onClose={handleCloseModal}
          onSend={handleSend}
          messages={messages}
          setMessages={setMessages}
          setChatUuid={setChatUuid}
          isLoading={isLoading}
          streamedMessage={streamedText ?? ""}
        />
      ) : (
        <ChatPromptInput onSend={handleSend} />
      )}
    </div>
  );
};

export default AIAssistant;
